import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { SidebarNavChildren } from './SidebarNavChildren';
import { NavItem } from 'types/sidebar';

type SidebarNavDivProps = {
  item: NavItem;
  level?: number;
  isCollapsed?: boolean;
  onExpandSidebar?: () => void;
  setSidebarOpen?: (value: boolean) => void;
};

export const SidebarNavDiv = ({
  item,
  level = 0,
  isCollapsed = false,
  onExpandSidebar,
  setSidebarOpen,
}: SidebarNavDivProps) => {
  const [showChildren, setShowChildren] = useState(false);
  const location = useLocation();
  const isActive = location.pathname.split('/').includes(item.key);
  useEffect(() => {
    if (isActive) setShowChildren(true);
  }, [isActive, location.pathname]);
  const iconClassName =
    'text-slate-400 group-hover:text-slate-200 ml-6 flex-shrink-0 h-5 w-5';

  const paddingLeft =
    level === 0 ? 'px-3' : level === 1 ? 'pl-8 pr-3' : 'pl-12 pr-3';

  if (isCollapsed) {
    return (
      <button
        type="button"
        aria-expanded={false}
        style={{ width: 'calc(100% - 1rem)' }}
        onClick={() => {
          setShowChildren(true);
          onExpandSidebar?.();
        }}
        className={classNames(
          location.pathname.split('/').includes(item.key)
            ? 'bg-slate-700/50 text-slate-50'
            : 'text-slate-50 hover:bg-slate-700/50',
          'mx-2 rounded-md group flex items-center justify-center py-4 text-sm font-medium cursor-pointer',
          'px-2',
        )}
        title={item.name}
      >
        <item.icon
          className={classNames(
            location.pathname.split('/').includes(item.key)
              ? 'text-slate-50'
              : 'text-slate-400 group-hover:text-slate-200',
            'flex-shrink-0 h-6 w-6',
          )}
          aria-hidden="true"
        />
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-expanded={showChildren}
        style={{ width: 'calc(100% - 1rem)' }}
        className={classNames(
          location.pathname.split('/').includes(item.key)
            ? 'bg-slate-700/50 text-slate-50'
            : 'text-slate-50 hover:bg-slate-700/50',
          'mx-2 rounded-md group flex items-center py-4 text-sm font-medium cursor-pointer justify-between',
          paddingLeft,
        )}
        onClick={() => (item?.children ? setShowChildren(!showChildren) : null)}
      >
        <div className="group flex items-center">
          <item.icon
            className={classNames(
              location.pathname.split('/').includes(item.key)
                ? 'text-slate-50'
                : 'text-slate-400 group-hover:text-slate-200',
              'mr-3 flex-shrink-0',
              level === 0 ? 'h-6 w-6' : 'h-5 w-5',
            )}
            aria-hidden="true"
          />
          {item.name}
        </div>
        {item.children ? (
          !showChildren ? (
            <ChevronRightIcon className={iconClassName} />
          ) : (
            <ChevronDownIcon className={iconClassName} />
          )
        ) : null}
      </button>
      {item.children &&
        item.children.map((childItem) => (
          <SidebarNavChildren
            showChildren={showChildren}
            item={childItem}
            key={childItem.key}
            level={level + 1}
            setSidebarOpen={setSidebarOpen}
          />
        ))}
    </>
  );
};
